/**
 * K-OS Health → Workouts.
 *
 * Tabbed page per the K-OS Health design:
 *   Today          — active in-browser tracker (set logging, edit mode)
 *   Training phase — current/past phases, multi-pattern weeks, edit
 *   Library        — saved workout templates, start/edit
 *   History        — past sessions list + detail view
 *
 * The Today tab boots from the most-recent in_progress session if any.
 * Otherwise it shows a "Start a workout" picker that lets the user start
 * from any template — or land on the most recent completed session as a
 * read-only summary.
 */

import { useEffect, useMemo, useState } from 'react';
import { Icon } from '@k-os/ui';
import { useNavigate } from '@tanstack/react-router';
import { Route as WorkoutsRoute } from '../../routes/health/workouts';
import {
  useWorkouts,
  useActiveWorkouts,
  useWorkoutTemplates,
  useStartFromTemplate,
  useCreateWorkout,
  type WorkoutTemplateDto,
  type WorkoutSessionDto,
} from '../../api/health';
import { WorkoutTracker } from './workouts/WorkoutTracker';
import { PhaseManager } from './workouts/PhaseManager';
import { WorkoutLibrary } from './workouts/WorkoutLibrary';
import { WorkoutHistory } from './workouts/WorkoutHistory';
import { WorkoutDetail } from './workouts/WorkoutDetail';
import { WorkoutPlanned } from './workouts/WorkoutPlanned';
import { WorkoutTemplatePreview } from './workouts/WorkoutTemplatePreview';
import { WorkoutTemplateEditor } from './workouts/WorkoutTemplateEditor';
import { ActiveWorkoutChooser } from './workouts/ActiveWorkoutChooser';
import styles from './workouts/workouts.module.css';
import baseStyles from './health.module.css';

type Tab = 'today' | 'phase' | 'library' | 'planned' | 'history';

/**
 * Which full-page editor is open, if any. Editors fully replace the
 * tabs view (similar to WorkoutDetail) so the user sees a focused form.
 */
type EditorState =
  | null
  | { kind: 'new-template' }
  | { kind: 'edit-template'; template: WorkoutTemplateDto };

export function HealthWorkoutsScreen() {
  const search = WorkoutsRoute.useSearch();
  const navigate = useNavigate({ from: '/health/workouts' });
  const tab: Tab = search.tab ?? 'today';

  function setTab(next: Tab): void {
    // Navigate with a fresh search so the back button walks the user
    // through the tabs they actually visited.
    if (next === 'library') {
      void navigate({ search: { tab: 'library', sub: search.sub ?? 'workouts' } });
    } else {
      void navigate({ search: { tab: next } });
    }
  }

  const [activeSessionId, setActiveSessionId] = useState<string | null>(null);
  const [historyDetailId, setHistoryDetailId] = useState<string | null>(null);
  const [previewTemplateId, setPreviewTemplateId] = useState<string | null>(null);
  const [editor, setEditor] = useState<EditorState>(null);

  const workoutsQ = useWorkouts();
  const templatesQ = useWorkoutTemplates();
  const activeWorkoutsQ = useActiveWorkouts();
  const start = useStartFromTemplate();
  const createBlank = useCreateWorkout();

  // `?session=<id>` (set by the sidebar "Active workout" item and the
  // chooser below) pins that session into the tracker, overriding the
  // preference heuristic. Applied whenever the URL asserts a session that
  // isn't already active — not just once per distinct value — so that
  // re-asserting the same param via browser back/forward (after a manual
  // switch elsewhere changed activeSessionId without updating the URL)
  // still re-pins it.
  //
  // This is safe from ping-ponging with manual switches: every
  // setActiveSessionId call site in this component other than the chooser's
  // onChoose is immediately followed by setTab('today'), which navigates
  // with a *fresh* search object (see setTab above) that drops `session`
  // entirely — so a manual switch always clears the param rather than
  // leaving a stale, mismatched one in the URL. The chooser's onChoose is
  // the one site that keeps `session` in the URL, and it always sets it to
  // the same id it just activated. So this effect never fires immediately
  // after a manual switch with a *different* id still in the URL.
  useEffect(() => {
    const sessionParam = search.session ?? null;
    if (sessionParam && sessionParam !== activeSessionId) {
      setActiveSessionId(sessionParam);
    }
  }, [search.session, activeSessionId]);

  // Pick "today's session" with this preference order:
  //   1. anything the user explicitly pinned (e.g. clicked from Phase / Planned)
  //   2. an in-progress session (regardless of date — still being worked on)
  //   3. today's planned session (the phase-materialized one — the common case)
  //   4. today's completed session (if already done — opens read-only summary)
  //   5. null → empty-state placeholder appears
  const todaySession = useMemo(() => {
    if (activeSessionId) return activeSessionId;
    const list = workoutsQ.data?.workouts ?? [];
    const inProgress = list.find((w) => w.status === 'in_progress');
    if (inProgress) return inProgress.id;
    // Local YYYY-MM-DD — matches the date string stored on the session row
    // (which was created in the workspace's local context, not UTC).
    const today = new Date();
    const todayIso = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`;
    const plannedToday = list.find((w) => w.status === 'planned' && w.date === todayIso);
    if (plannedToday) return plannedToday.id;
    const completedToday = list.find((w) => w.status === 'completed' && w.date === todayIso);
    if (completedToday) return completedToday.id;
    return null;
  }, [activeSessionId, workoutsQ.data]);

  // History / Planned counts. "Planned" includes anything not yet completed
  // or skipped (planned + in_progress); History is the inverse — the only
  // statuses that represent a session you've actually done with.
  const { plannedCount, historyCount } = useMemo(() => {
    const list = workoutsQ.data?.workouts ?? [];
    let planned = 0;
    let history = 0;
    for (const w of list) {
      if (w.status === 'completed' || w.status === 'skipped') history += 1;
      else planned += 1;
    }
    return { plannedCount: planned, historyCount: history };
  }, [workoutsQ.data]);

  function startFromTemplate(t: WorkoutTemplateDto) {
    start.mutate(
      { id: t.id },
      {
        onSuccess: (res) => {
          // Closing the preview (if open) before swapping in the tracker
          // keeps the early-return branch order predictable.
          setPreviewTemplateId(null);
          setActiveSessionId(res.sessionId);
          setTab('today');
        },
      },
    );
  }

  function startBlankSession() {
    const today = new Date().toISOString().slice(0, 10);
    createBlank.mutate(
      {
        date: today,
        name: 'Workout',
        status: 'in_progress',
        startTime: new Date().toISOString(),
        type: 'strength',
      },
      {
        onSuccess: (res) => {
          setActiveSessionId(res.workout.id);
          setTab('today');
        },
      },
    );
  }

  // History detail view takes over the whole page.
  if (historyDetailId) {
    return (
      <div className={baseStyles.page}>
        <WorkoutDetail
          sessionId={historyDetailId}
          onBack={() => setHistoryDetailId(null)}
        />
      </div>
    );
  }

  // Template preview takes over the whole page, just like history detail.
  // The user lands here when tapping a template tile body on the Today
  // tab; the Start button still bypasses the preview for a one-tap
  // commit, so this screen is opt-in.
  if (previewTemplateId) {
    return (
      <div className={baseStyles.page}>
        <WorkoutTemplatePreview
          templateId={previewTemplateId}
          onStart={startFromTemplate}
          onBack={() => setPreviewTemplateId(null)}
          isStarting={start.isPending}
        />
      </div>
    );
  }

  // Editor (workout template) takes over the page so the user
  // has a focused, breadcrumb-titled form. Closes back to the Library tab.
  if (editor) {
    return (
      <div className={baseStyles.page}>
        {editor.kind === 'new-template' && (
          <WorkoutTemplateEditor
            onCancel={() => setEditor(null)}
            onSaved={() => setEditor(null)}
          />
        )}
        {editor.kind === 'edit-template' && (
          <WorkoutTemplateEditor
            template={editor.template}
            onCancel={() => setEditor(null)}
            onSaved={() => setEditor(null)}
          />
        )}
      </div>
    );
  }

  return (
    <div className={baseStyles.page}>
      <header className={baseStyles.head}>
        <div>
          <h2 className={baseStyles.title}>Workouts</h2>
          <div className={baseStyles.subtitle}>
            Log workouts in the browser, manage your training phase, save and
            reuse templates, and browse what you've done.
          </div>
        </div>
        <div className={baseStyles.actions}>
          {tab === 'library' && (
            <button
              type="button"
              className="kos-btn kos-btn-primary"
              onClick={() => setEditor({ kind: 'new-template' })}
            >
              <Icon name="plus" size={11} /> New template
            </button>
          )}
          {tab === 'today' && !todaySession && (
            <button type="button" className="kos-btn" onClick={startBlankSession}>
              <Icon name="plus" size={11} /> Blank session
            </button>
          )}
        </div>
      </header>

      <div className={styles.subnav} role="tablist">
        <Tab id="today" current={tab} onClick={setTab}>
          Today
        </Tab>
        <Tab id="phase" current={tab} onClick={setTab}>
          Training phase
        </Tab>
        <Tab id="library" current={tab} onClick={setTab}>
          Library{' '}
          <span className={styles.subnavCount}>
            {templatesQ.data?.templates.length ?? 0}
          </span>
        </Tab>
        <Tab id="planned" current={tab} onClick={setTab}>
          Planned{' '}
          <span className={styles.subnavCount}>
            {plannedCount}
          </span>
        </Tab>
        <Tab id="history" current={tab} onClick={setTab}>
          History{' '}
          <span className={styles.subnavCount}>
            {historyCount}
          </span>
        </Tab>
      </div>

      {tab === 'today' &&
        (search.choose ? (
          <ActiveWorkoutChooser
            sessions={activeWorkoutsQ.data?.workouts ?? []}
            isLoading={activeWorkoutsQ.isLoading}
            onChoose={(id) => {
              setActiveSessionId(id);
              void navigate({ search: { tab: 'today', session: id } });
            }}
          />
        ) : todaySession ? (
          <WorkoutTracker sessionId={todaySession} />
        ) : (
          <NoSessionPlaceholder
            templates={templatesQ.data?.templates ?? []}
            onPick={startFromTemplate}
            onPreview={(t) => setPreviewTemplateId(t.id)}
            onBlank={startBlankSession}
            recentSessions={workoutsQ.data?.workouts ?? []}
            onOpenRecent={(w) => setHistoryDetailId(w.id)}
          />
        ))}
      {tab === 'phase' && (
        <PhaseManager
          onOpenWorkout={(w) => {
            // Clicking a workout in the day-plan should open it for editing
            // (planned/in-progress in the tracker, completed/skipped in the
            // read-only detail view).
            if (w.status === 'completed' || w.status === 'skipped') {
              setHistoryDetailId(w.id);
            } else {
              setActiveSessionId(w.id);
              setTab('today');
            }
          }}
        />
      )}
      {tab === 'library' && (
        <WorkoutLibrary
          onOpenTemplate={(t) => setEditor({ kind: 'edit-template', template: t })}
          onStartSession={(sid) => {
            setActiveSessionId(sid);
            setTab('today');
          }}
          onNewTemplate={() => setEditor({ kind: 'new-template' })}
        />
      )}
      {tab === 'planned' && (
        <WorkoutPlanned
          onOpen={(w) => {
            // Planned + in_progress workouts open in the editable tracker on
            // the Today tab. setActiveSessionId pins this session.
            setActiveSessionId(w.id);
            setTab('today');
          }}
        />
      )}
      {tab === 'history' && (
        <WorkoutHistory onOpen={(w) => setHistoryDetailId(w.id)} />
      )}
    </div>
  );
}

function Tab({
  id,
  current,
  onClick,
  children,
}: {
  id: Tab;
  current: Tab;
  onClick: (t: Tab) => void;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      role="tab"
      aria-selected={current === id}
      className={`${styles.subnavItem} ${current === id ? styles.subnavActive : ''}`}
      onClick={() => onClick(id)}
    >
      {children}
    </button>
  );
}

function NoSessionPlaceholder({
  templates,
  onPick,
  onPreview,
  onBlank,
  recentSessions,
  onOpenRecent,
}: {
  templates: WorkoutTemplateDto[];
  onPick: (t: WorkoutTemplateDto) => void;
  onPreview: (t: WorkoutTemplateDto) => void;
  onBlank: () => void;
  recentSessions: WorkoutSessionDto[];
  onOpenRecent: (w: WorkoutSessionDto) => void;
}) {
  const recent = recentSessions.slice(0, 3);
  return (
    <div>
      <div
        style={{
          padding: '24px 20px',
          border: '1px dashed var(--line)',
          borderRadius: 10,
          background: 'var(--surface-2)',
          textAlign: 'center',
          marginBottom: 22,
        }}
      >
        <div style={{ fontSize: 16, fontWeight: 600, marginBottom: 6 }}>
          No workout in progress.
        </div>
        <div style={{ fontSize: 14, color: 'var(--ink-3)', marginBottom: 16 }}>
          Start one from a template below — or kick off a blank session and add
          exercises on the fly.
        </div>
        <button type="button" className="kos-btn" onClick={onBlank}>
          <Icon name="plus" size={11} /> Start blank session
        </button>
      </div>

      {templates.length > 0 && (
        <div>
          <h3
            style={{
              margin: '0 0 10px',
              fontSize: 14,
              fontWeight: 600,
              letterSpacing: '0.06em',
              textTransform: 'uppercase',
              color: 'var(--ink-3)',
            }}
          >
            Start from a template
          </h3>
          <div className={styles.libGrid}>
            {templates.slice(0, 6).map((t) => (
              <div
                key={t.id}
                className={styles.libCard}
                role="button"
                tabIndex={0}
                onClick={() => onPreview(t)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    onPreview(t);
                  }
                }}
                style={{ cursor: 'pointer' }}
                aria-label={`Preview ${t.name}`}
              >
                <div className={styles.libCardHead}>
                  <div className={styles.libCardName}>{t.name}</div>
                </div>
                {t.description && <div className={styles.libCardDesc}>{t.description}</div>}
                <div className={styles.libCardMeta}>
                  {t.durationMin && (
                    <span>
                      <strong>{t.durationMin}</strong> min
                    </span>
                  )}
                  <span>
                    <strong>{t.exercises.length}</strong> exercises
                  </span>
                  <span>
                    used <strong>{t.usedCount}</strong>×
                  </span>
                </div>
                <div className={styles.libCardActions}>
                  <button
                    type="button"
                    className="kos-btn kos-btn-primary"
                    style={{ flex: 1 }}
                    onClick={(e) => {
                      // The card itself opens the preview; the Start button
                      // shortcuts straight into the session for users who
                      // already know what's in this template.
                      e.stopPropagation();
                      onPick(t);
                    }}
                  >
                    <Icon name="check" size={11} /> Start
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {recent.length > 0 && (
        <div style={{ marginTop: 24 }}>
          <h3
            style={{
              margin: '0 0 10px',
              fontSize: 14,
              fontWeight: 600,
              letterSpacing: '0.06em',
              textTransform: 'uppercase',
              color: 'var(--ink-3)',
            }}
          >
            Recent workouts
          </h3>
          {recent.map((w) => (
            <div
              key={w.id}
              className={styles.histRow}
              onClick={() => onOpenRecent(w)}
            >
              <div className={styles.histDate}>
                <div className={styles.histDay}>{new Date(w.date).toLocaleDateString(undefined, { weekday: 'short', day: 'numeric', month: 'short' })}</div>
              </div>
              <div>
                <div className={styles.histName}>{w.name}</div>
                <div className={styles.histMeta}>{w.type} · {w.status}</div>
              </div>
              <Icon name="arrowRight" size={12} />
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
